/**
 * Vocabolario di dominio.
 *
 * Questo file è deliberatamente privo di logica: definisce i termini con cui
 * tutto il sistema parla di finalità, consensi e stati. Le regole privacy si
 * appoggiano a queste costanti, quindi ogni aggiunta qui va valutata rispetto
 * alla specifica (docs/FAIR_LEAD_COLLECTOR_GDPR_DEVELOPMENT_SPEC_v1.1.md).
 */
/* -------------------------------------------------------------------------- */
/* Finalità (§2)                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Le tre finalità sono tecnicamente distinte e non vanno mai collassate in un
 * unico flag (§2, §35.1).
 *
 * - DOCUMENTATION_REQUEST — invio della documentazione richiesta (§2.1)
 * - COMMERCIAL_CALLBACK   — ricontatto commerciale richiesto (§2.2)
 * - EMAIL_MARKETING       — marketing via e-mail, richiede consenso (§2.3)
 */
export const PURPOSES = ['DOCUMENTATION_REQUEST', 'COMMERCIAL_CALLBACK', 'EMAIL_MARKETING'];
/**
 * Finalità che NON possono essere soddisfatte senza un consenso esplicito.
 * Serve a rendere impossibile, per costruzione, trattare il marketing come le
 * altre due finalità.
 */
export const CONSENT_REQUIRING_PURPOSES = ['EMAIL_MARKETING'];
/* -------------------------------------------------------------------------- */
/* Consenso (§3, §6.6)                                                        */
/* -------------------------------------------------------------------------- */
/**
 * `consent_type` è estensibile per progetto (§3): nuovi tipi si aggiungono solo
 * se approvati dal Titolare.
 */
export const CONSENT_TYPES = ['EMAIL_MARKETING', 'EMAIL_TRACKING'];
/**
 * Tipi che il kiosk può effettivamente raccogliere nell'MVP.
 *
 * EMAIL_TRACKING esiste nel modello dati per estensibilità, ma ADR-008 vieta
 * il tracking individuale nell'MVP: il kiosk non deve mai generare un evento di
 * questo tipo. Il vincolo è applicato in `grant-consent` lato server, non solo
 * nella UI.
 */
export const KIOSK_COLLECTABLE_CONSENT_TYPES = ['EMAIL_MARKETING'];
/**
 * Il consenso è un log append-only: si aggiungono eventi, non si aggiorna un
 * boolean (§6.6, §35.5). Lo stato corrente è derivato dall'ultimo evento valido.
 */
export const CONSENT_ACTIONS = ['GRANTED', 'WITHDRAWN'];
/**
 * Origine di un evento di consenso.
 *
 * - FAIR_KIOSK          — azione esplicita del visitatore al kiosk
 * - ADMIN_CORRECTION    — correzione amministrativa: richiede reason e audit,
 *                         e non equivale a un consenso prestato dal visitatore (§22)
 * - SUBJECT_REQUEST     — revoca/opposizione richiesta dall'interessato (§14)
 */
export const CONSENT_SOURCES = ['FAIR_KIOSK', 'ADMIN_CORRECTION', 'SUBJECT_REQUEST'];
/* -------------------------------------------------------------------------- */
/* Richieste di informazioni (§6.5)                                           */
/* -------------------------------------------------------------------------- */
export const INFORMATION_REQUEST_TYPES = [
    'DOCUMENT',
    'CATALOG',
    'PRODUCT_INFO',
    'COMMERCIAL_CALLBACK',
];
/**
 * Tipi che una fiera può offrire come materiale richiedibile.
 *
 * `COMMERCIAL_CALLBACK` è deliberatamente escluso: il ricontatto commerciale è
 * una finalità distinta, con la propria casella e la propria descrizione
 * nell'informativa, non un materiale da mettere in elenco. Se una fiera potesse
 * configurarlo come voce, si aprirebbe una seconda via — senza etichetta
 * dedicata e senza la separazione visiva prevista da §2.2 — verso una finalità
 * che il visitatore deve poter riconoscere come tale.
 */
export const REQUESTABLE_ITEM_TYPES = [
    'DOCUMENT',
    'CATALOG',
    'PRODUCT_INFO',
];
/**
 * A quale finalità risponde ogni tipo di richiesta.
 *
 * Serve alla retention: il periodo di conservazione è configurato per finalità
 * (§13), mentre ciò che il visitatore ha effettivamente chiesto è registrato per
 * tipo. Senza questa mappatura esplicita la corrispondenza finirebbe sparsa in
 * più punti, e un tipo nuovo rischierebbe di non scadere mai — il record di
 * dettaglio è esaustivo, quindi aggiungere un tipo senza dichiararne la
 * finalità non compila.
 */
export const PURPOSE_BY_REQUEST_TYPE = {
    DOCUMENT: 'DOCUMENTATION_REQUEST',
    CATALOG: 'DOCUMENTATION_REQUEST',
    PRODUCT_INFO: 'DOCUMENTATION_REQUEST',
    COMMERCIAL_CALLBACK: 'COMMERCIAL_CALLBACK',
};
/**
 * Finalità soddisfatta da un consenso di questo tipo, se esiste.
 *
 * `EMAIL_TRACKING` non è una finalità dell'MVP (ADR-008): non ha un periodo di
 * conservazione perché non deve esistere alcun dato raccolto su quella base.
 */
export const PURPOSE_BY_CONSENT_TYPE = {
    EMAIL_MARKETING: 'EMAIL_MARKETING',
    EMAIL_TRACKING: null,
};
/* -------------------------------------------------------------------------- */
/* Ruoli e stati                                                              */
/* -------------------------------------------------------------------------- */
/** Il visitatore non è un utente applicativo (§6.1). */
export const USER_ROLES = ['ADMIN', 'OPERATOR'];
export const EVENT_STATUSES = ['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED'];
/** Solo una fiera ACTIVE accetta nuovi lead (§19). */
export const LEAD_ACCEPTING_EVENT_STATUSES = ['ACTIVE'];
export const DEVICE_STATUSES = ['PENDING', 'ACTIVE', 'REVOKED'];
/** Stati della coda locale offline (§9.2). Vive sul client. */
export const SYNC_STATUSES = ['PENDING', 'SYNCING', 'SYNCED', 'FAILED'];
/* -------------------------------------------------------------------------- */
/* Retention (§13)                                                            */
/* -------------------------------------------------------------------------- */
export const RETENTION_ACTIONS = ['DELETE', 'ANONYMIZE', 'REVIEW'];
//# sourceMappingURL=vocabulary.js.map